import { ReactNode } from 'react'

import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { Id } from '../types'
import { toCellId } from '../utils'

type TableCellProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    col: TCol
    row: TRow
    children: ReactNode
    cellClassName?: (row: TRow, col: TCol) => string
    'data-test'?: string
    height?: number
    /**
     * Optional action button to render in the top-right corner of the cell.
     * Will appear on hover/focus with the same behavior as the Cell component's edit button.
     */
    actionButton?: (row: TRow, col: TCol) => ReactNode
}

// Wrapper component that conditionally renders the appropriate cell type
export const TableCell = <TCol extends { id: Id }, TRow extends { id: Id }>({
    col,
    row,
    children,
    cellClassName,
    height,
    actionButton,
    'data-test': dataTest,
}: TableCellProps<TCol, TRow>) => {
    const cellId = toCellId({ rowId: row.id, colId: col.id })

    return (
        <td
            id={cellId}
            data-test={dataTest}
            data-cell={`${row.id}_${col.id}`}
            data-row-id={row.id}
            data-col-id={col.id}
            style={{ height }}
            className={astraTwMerge(
                'group/cell relative h-full min-w-[28px] cursor-pointer self-stretch rounded-none border-r bg-surface-base p-1 outline-none transition-all last:border-r-0',
                cellClassName?.(row, col)
            )}
        >
            {children}
            {actionButton && (
                <div
                    className="absolute right-1 top-1 opacity-0 shadow transition-opacity focus-within:opacity-100 hover:opacity-100 focus:opacity-100 group-hover/cell:opacity-100"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {actionButton(row, col)}
                </div>
            )}
        </td>
    )
}
